import { computed, type ComputedRef } from 'vue'

import type { ChangeType, ReportColumn } from '../models/column.types'
import { mapWorkspaceColumn } from '../service/columns/mapWorkspaceColumn'
import { prettifyField } from '../service/columns/normalizeDefinitions'
import { useMetricCatalogStore, type MetricCatalogStatus } from '../stores/metricCatalogStore'

import { useWorkspace } from '@/features/account'
import { i18n } from '@/shared/i18n'

/** v3 columnStore header-override parity (contract 08 §7), now i18n keys. Static `t()` calls
 *  so `@intlify/vue-i18n/no-unused-keys` can see the usage. */
function headerOverride(field: string): string | null {
  if (field === 'external_action') return i18n.global.t('reports.columns.externalAction')
  if (field === 'ad_category') return i18n.global.t('reports.columns.adCategory')
  return null
}

export interface UseMetricCatalog {
  columns: ComputedRef<Record<string, ReportColumn>>
  currencyMetrics: ComputedRef<ReportColumn[]>
  status: ComputedRef<MetricCatalogStatus>
  getColumn: (field: string) => ReportColumn | null
  metricLabel: (field: string) => string
  changeType: (field: string) => ChangeType
  isLowerBetter: (field: string) => boolean | null
  isPercentage: (field: string) => boolean
}

/** The catalog reader surface modules 10/13/28 consume (contract 08 §9). Labels degrade to a
 *  prettified key on a catalog miss — never blank, never a toast (08 §12). */
export function useMetricCatalog(): UseMetricCatalog {
  const store = useMetricCatalogStore()
  const workspace = useWorkspace()

  // Several embedding features provide a deliberately narrow account mock in isolation. Treat a
  // missing attribute reader like an unhydrated workspace so catalog labels still degrade safely.
  const attributeValue = (key: string): unknown => workspace.getAttribute?.(key)?.value

  function getColumn(field: string): ReportColumn | null {
    return mapWorkspaceColumn(field, store.getColumn(field), attributeValue)
  }

  function metricLabel(field: string): string {
    return headerOverride(field) ?? getColumn(field)?.header ?? prettifyField(field)
  }

  function changeType(field: string): ChangeType {
    return getColumn(field)?.change_type ?? null
  }

  /** Tri-state: boolean when the catalog knows the direction, null = defer to the caller's
   *  fallback (e.g. anomalies' static polarity list). `notr` already folded to null (§6.1). */
  function isLowerBetter(field: string): boolean | null {
    const direction = changeType(field)
    return direction === null ? null : direction === 'negative'
  }

  function isPercentage(field: string): boolean {
    const column = getColumn(field)
    return column?.percentage === true || column?.sub_type === 'percentage'
  }

  return {
    columns: computed(() =>
      Object.fromEntries(
        Object.keys(store.columns).flatMap((field) => {
          const column = getColumn(field)
          return column ? [[field, column]] : []
        }),
      ),
    ),
    currencyMetrics: computed(() =>
      Object.values(store.columns)
        .map((column) => getColumn(column.field))
        .filter((column): column is ReportColumn => column?.sub_type === 'currency'),
    ),
    status: computed(() => store.status),
    getColumn,
    metricLabel,
    changeType,
    isLowerBetter,
    isPercentage,
  }
}
